import React, { useState } from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import { UseCases } from "@planzone/core";
import { useAssignTag, useListEdition } from "@planzone/view-models";
import { Assignable, Removable, list as listMock } from "@planzone/dto";

import { ActivityWatcher } from "@common/activityWatcher";
import { useToggle } from "@common/treeToggle";
import { useModal } from "@root/client/modal";
import { ListToggleArrow } from "@common/listToggleArrow";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { AllocationUnit } from "@common/allocationUnit";
import { useIsElementHovered } from "@common/elementHover";
import { CommentsCount } from "../commentsCount";
import { AttachmentsCount } from "../attachmentsCount";
import { SecondaryCriteria } from "../secondaryCriteria";
import { useProjectCtx } from "../projectCtx";
import { reCalculateDateRange } from "../utils";
import { Tags } from "../tags";
import { DateForm } from "../dateForm";
import { Props } from "./types";
import { ContextualMenu } from "./contextualMenu";
import styles from "./styles.scss";

function toFixed(nbr: number) {
  return typeof nbr === "number" || !isNaN(parseFloat(nbr))
    ? Math.round(nbr) === nbr
      ? nbr
      : nbr.toFixed(2)
    : "";
}

function List({
  list,
  meta,
  parentList,
  addIn,
  addAfter,
  isMultiProject,
  minDate,
  maxDate,
}: Props) {
  const realList = { ...listMock, ...list };
  const userTimezone = DateTime.local().zoneName;
  const { isOpen, toggle } = useToggle(list.uuid, list.depth + 1, true);
  const { pushView } = useModal();
  const { project, template } = useProjectCtx();
  const projectTimezone = project.timeZone || userTimezone;
  const isHovered = useIsElementHovered(list.uuid);
  const { assignTag } = useAssignTag();
  const { isRequestPending, editList } = useListEdition();
  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const recalculated = reCalculateDateRange(list, minDate, maxDate);

  ActivityWatcher.useActivityWatcher(ref, {
    focus: () => setShowContextualMenu(!showContextualMenu),
    blur: () => setShowContextualMenu(false),
  });

  return (
    <div
      className={classNames(styles.list, {
        [styles.hovered]: isHovered,
      })}
    >
      <SecondaryCriteria project={project}>
        {() => ({
          main: (
            <div className={styles.main}>
              <div className={styles.iconBox} onClick={toggle}>
                <ListToggleArrow isOpen={isOpen} />
              </div>
              <h3
                className={styles.name}
                onClick={() =>
                  pushView({
                    key: UseCases.retrieveList,
                    data: { listId: list.uuid, isMultiProject },
                  })
                }
              >
                {list.name}{" "}
                <span className={styles.elementsNbr}>{meta.elementCount}</span>
              </h3>
            </div>
          ),
          criteria: template.reduce(
            (acc, criteria) => ({
              ...acc,
              [criteria.uuid]: (
                <SecondaryCriteriaForm
                  criteria={criteria}
                  value={
                    list.attributes ? list.attributes[criteria.uuid] || "" : ""
                  }
                  projectTimezone={projectTimezone}
                  disabled={isRequestPending}
                  submit={(value) =>
                    editList({
                      list: realList,
                      nextList: {
                        ...realList,
                        attributes: {
                          ...list.attributes,
                          [criteria.uuid]: value,
                        },
                      },
                    })
                  }
                />
              ),
            }),
            {
              tags: (
                <p className={styles.tags}>
                  <Tags
                    tags={list.tags}
                    project={project}
                    removable={Removable.LIST}
                    uuid={list.uuid}
                    add={(tag) =>
                      assignTag({
                        tag,
                        assignable: Assignable.LIST,
                        assignableId: list.uuid,
                        project,
                      })
                    }
                  />
                </p>
              ),
              allocated: (
                <div className={styles.allocated}>
                  {toFixed(meta.allocatedHours)}
                  <AllocationUnit
                    amount={meta.allocatedHours}
                    format="symbol"
                  />
                </div>
              ),
              spent: (
                <div className={styles.allocated}>
                  {toFixed(meta.spentHours)}
                  <AllocationUnit amount={meta.spentHours} format="symbol" />
                </div>
              ),
              startDate: (
                <div className={styles.endDate}>
                  <DateForm
                    projectElement={list}
                    projectTimezone={projectTimezone}
                    date={list.startDate}
                    recalculatedDate={recalculated.startDate}
                    disabled={isRequestPending}
                    minDate={(() => {
                      const bound = list.minStartDate;
                      if (!bound) return undefined;
                      const dt = DateTime.fromISO(bound, {
                        zone: "utc",
                      }).setZone(userTimezone);
                      return dt.isValid
                        ? { year: dt.year, month: dt.month - 1, day: dt.day }
                        : undefined;
                    })()}
                    maxDate={(() => {
                      const bound =
                        list.maxStartDate || list.endDate || list.maxEndDate;
                      if (!bound) return undefined;
                      const dt = DateTime.fromISO(bound, {
                        zone: "utc",
                      }).setZone(userTimezone);
                      return dt.isValid
                        ? { year: dt.year, month: dt.month - 1, day: dt.day }
                        : undefined;
                    })()}
                    submit={(startDate) =>
                      editList({
                        list: realList,
                        nextList: { ...realList, startDate },
                      })
                    }
                  />
                </div>
              ),
              endDate: (
                <div className={styles.endDate}>
                  <DateForm
                    projectElement={list}
                    projectTimezone={projectTimezone}
                    date={list.endDate}
                    recalculatedDate={recalculated.endDate}
                    disabled={isRequestPending}
                    minDate={(() => {
                      const bound =
                        list.minEndDate || list.startDate || list.minStartDate;
                      if (!bound) return undefined;
                      const dt = DateTime.fromISO(bound, {
                        zone: "utc",
                      }).setZone(userTimezone);
                      return dt.isValid
                        ? { year: dt.year, month: dt.month - 1, day: dt.day }
                        : undefined;
                    })()}
                    submit={(endDate) =>
                      editList({
                        list: realList,
                        nextList: { ...realList, endDate },
                      })
                    }
                  />
                </div>
              ),
            },
          ),
          comments: (
            <CommentsCount
              subject={list.uuid}
              type="list"
              commentsNbr={meta.commentCount}
            />
          ),
          files: (
            <AttachmentsCount uuid={list.uuid} fileNbr={meta.attachmentCount} />
          ),
          contextualMenu: (
            <div
              ref={ref}
              className={classNames(styles.contextualMenu, {
                [styles.displayedContextualMenu]: showContextualMenu,
              })}
            >
              <ContextualMenu
                list={realList}
                parentList={parentList}
                project={project}
                addIn={addIn}
                addAfter={addAfter}
              />
            </div>
          ),
        })}
      </SecondaryCriteria>
    </div>
  );
}

export { List };
